import { useState, useMemo } from 'react';
import { type UseFormSetError } from 'react-hook-form';
import {
  useReasonTypes,
  useCreateReasonType,
  useUpdateReasonType,
  useDeleteReasonType,
  useReasonTypeById,
} from '../../../hooks/queries/useVisitorManagement';
import type {
  VisitReasonType,
  VisitReasonTypeWrite,
} from '../../../types/VisitorManagementTypes';
import { toaster } from '../../ui/toaster';
import { ApiValidationError } from '../../../lib/http-client';
import { StatusBadge, type Column, type StatusType } from '@themes/aegix';
import { useSearchParams } from 'react-router-dom';

interface ReasonRow {
  [key: string]: unknown;
  id: string;
  name: string;
  code: string;
  requires_host: boolean;
  requires_escort: boolean;
  is_active: boolean;
}

const columns: Column<ReasonRow>[] = [
  { key: 'name', label: 'Name', sortable: true, width: '180px' },
  { key: 'code', label: 'Code', sortable: true, width: '120px' },
  {
    key: 'requires_host',
    label: 'Requires Host',
    width: '120px',
    render: (row) =>
      row.requires_host ? (
        <StatusBadge status={'active' as StatusType} label="Yes" />
      ) : null,
  },
  {
    key: 'requires_escort',
    label: 'Requires Escort',
    width: '130px',
    render: (row) =>
      row.requires_escort ? (
        <StatusBadge status={'warning' as StatusType} label="Yes" />
      ) : null,
  },
  {
    key: 'is_active',
    label: 'Active',
    width: '100px',
    render: (row) => (
      <StatusBadge
        status={
          row.is_active ? ('active' as StatusType) : ('inactive' as StatusType)
        }
        label={row.is_active ? 'Active' : 'Inactive'}
      />
    ),
  },
];

export function useReasonTypeCrud() {
  const [searchParams] = useSearchParams();
  const urlId = searchParams.get('public_id');
  const urlTab = searchParams.get('subtab');
  const isReasonTypeTab =
    !urlTab || urlTab.toLowerCase().replace(/\s+/g, '-') === 'visit-reasons';
  const { data: reasonById, error: reasonByIdError } = useReasonTypeById(
    urlId as string,
    isReasonTypeTab,
  );
  const { data, isLoading, error } = useReasonTypes();
  const createMutation = useCreateReasonType();
  const updateMutation = useUpdateReasonType();
  const deleteMutation = useDeleteReasonType();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editItem, setEditItem] = useState<VisitReasonType | null>(null);
  const isSingleReasonTypeView = Boolean(
    urlId && isReasonTypeTab && reasonById && !reasonByIdError,
  );
  const reasonDetails =
    isSingleReasonTypeView && reasonById ? [reasonById] : (data ?? []);
  const rows: ReasonRow[] = reasonDetails
    .filter((r): r is VisitReasonType => !!r)
    .map((r) => ({
      id: r.public_id,
      name: r.name,
      code: r.code,
      requires_host: r.requires_host,
      requires_escort: r.requires_escort,
      is_active: r.is_active,
    }));

  const handleClose = () => {
    setDrawerOpen(false);
    setEditItem(null);
  };

  const handleRowClick = (row: ReasonRow) => {
    const item = data?.find((r) => r.public_id === row.id);
    if (item) {
      setEditItem(item);
      setDrawerOpen(true);
    }
  };

  const handleSubmit = (
    values: VisitReasonTypeWrite,
    setError: UseFormSetError<VisitReasonTypeWrite>,
  ) => {
    const onError = (err: Error) => {
      if (err instanceof ApiValidationError) {
        for (const [field, messages] of Object.entries(err.fieldErrors)) {
          setError(field as keyof VisitReasonTypeWrite, {
            message: messages.join(', '),
          });
        }
      }
      toaster.create({
        title: 'Save failed',
        description: err.message,
        type: 'error',
      });
    };

    if (editItem) {
      updateMutation.mutate(
        { publicId: editItem.public_id, data: values },
        {
          onSuccess: () => {
            toaster.create({ title: 'Reason type updated', type: 'success' });
            handleClose();
          },
          onError,
        },
      );
    } else {
      createMutation.mutate(values, {
        onSuccess: () => {
          toaster.create({ title: 'Reason type created', type: 'success' });
          handleClose();
        },
        onError,
      });
    }
  };

  const handleDelete = () => {
    if (!editItem) return;
    deleteMutation.mutate(editItem.public_id, {
      onSuccess: () => {
        toaster.create({ title: 'Reason type deleted', type: 'success' });
        handleClose();
      },
      onError: (err) =>
        toaster.create({
          title: 'Delete failed',
          description: err.message,
          type: 'error',
        }),
    });
  };

  const initialValues: VisitReasonTypeWrite = useMemo(
    () =>
      editItem
        ? {
            name: editItem.name,
            code: editItem.code,
            description: editItem.description,
            requires_host: editItem.requires_host,
            requires_escort: editItem.requires_escort,
            is_active: editItem.is_active,
          }
        : {
            name: '',
            code: '',
            requires_host: false,
            requires_escort: false,
            is_active: true,
          },
    [editItem],
  );

  return {
    data: reasonDetails,
    isLoading,
    error,
    drawerOpen,
    setDrawerOpen,
    editItem,
    handleClose,
    handleSubmit,
    handleDelete,
    handleRowClick,
    initialValues,
    rows,
    columns,
  };
}
